import {
	AtlasAttachmentLoader,
	SkeletonJson,
	Spine,
	TextureAtlas,
} from '@pixi-spine/all-4.0'
import * as PIXI from 'pixi.js-legacy'
import { isLocalhost } from '../../../framework/src/helpers'
import SystemConfig, {
	CONFIG_PARAMS,
} from '../../../framework/src/system/SystemConfig'
export class JokerBonusHelpers {
	static getConfigUrl() {
		if (isLocalhost()) {
			return 'https://games.kajotgames.dev/config/joker_bonus'
		}
		return '/config/joker_bonus'
	}
	static circularGradientTexture(
		radius: number,
		colorStops: string[]
	): PIXI.Texture {
		const diameter = radius * 2
		const ctx = JokerBonusHelpers.createCanvasContext(diameter, diameter)
		const grd = ctx.createRadialGradient(
			radius,
			radius,
			radius / 2,
			radius,
			radius,
			radius - 10
		)

		JokerBonusHelpers.defineGradientColorStops(grd, colorStops)

		return JokerBonusHelpers.getTextureFromFillStyle(
			diameter,
			diameter,
			grd,
			ctx
		)
	}
	/**
	 * @static
	 * @param {number} w width of texture
	 * @param {number} h height of texture
	 * @param {number} angle angle of linear gradient in degrees, 0 is to the right, then going clockwise
	 * @param {string[]} colors color stops of gradient, those color stops will be spaced equally
	 * @return {PIXI.Texture}
	 * @memberof JokerBonusHelpers
	 */
	static linearGradientTexture(
		w: number,
		h: number,
		angle: number,
		colors: string[],
		colorStops?: number[]
	): PIXI.Texture {
		const radAngle = angle * (Math.PI / 180)
		const ctx = JokerBonusHelpers.createCanvasContext(w, h)
		const midpoint = {
			x: w / 2,
			y: h / 2,
		}
		const A = {
			x: midpoint.x + Math.cos(radAngle + Math.PI) * (w / 2),
			y: midpoint.y + Math.sin(radAngle + Math.PI) * (h / 2),
		}
		const B = {
			x: midpoint.x + Math.cos(radAngle) * (w / 2),
			y: midpoint.y + Math.sin(radAngle) * (h / 2),
		}
		const grd = ctx.createLinearGradient(A.x, A.y, B.x, B.y)

		JokerBonusHelpers.defineGradientColorStops(grd, colors, colorStops)

		return JokerBonusHelpers.getTextureFromFillStyle(w, h, grd, ctx)
	}
	static solidFillTexture(w: number, h: number, color: string): PIXI.Texture {
		const ctx = JokerBonusHelpers.createCanvasContext(w, h)
		return JokerBonusHelpers.getTextureFromFillStyle(w, h, color, ctx)
	}

	protected static createCanvasContext(
		width: number,
		height: number
	): CanvasRenderingContext2D {
		const c = document.createElement('canvas')
		c.width = width
		c.height = height
		return c.getContext('2d')
	}
	protected static getTextureFromFillStyle(
		width: number,
		height: number,
		style: string | CanvasGradient | CanvasPattern,
		ctx: CanvasRenderingContext2D
	): PIXI.Texture {
		ctx.fillStyle = style
		ctx.fillRect(0, 0, width, height)
		return PIXI.Texture.from(ctx.canvas)
	}
	protected static defineGradientColorStops(
		gradient: CanvasGradient,
		colors: string[],
		colorStops?: number[]
	) {
		colors.forEach((color, index) => {
			const offset = colorStops?.[index] ?? (100 / (colors.length - 1)) * index
			gradient.addColorStop(parseFloat((offset / 100).toFixed(4)), color)
		})
	}
	static formatNumber(x: string): string {
		const parts = x.split('.')
		parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
		return parts.join('.').replace(/0/g, 'O')
	}

	static getUrl(url: string, systemConfig: SystemConfig) {
		let cdnUrl = systemConfig.get(CONFIG_PARAMS.URL_CDN)
		if (cdnUrl && !isLocalhost()) {
			return (
				cdnUrl +
				systemConfig.get(CONFIG_PARAMS.GAME_ID) +
				'/' +
				url.replace('assets/', '')
			)
		} else {
			return url
		}
	}
	static getSpine(
		name: string,
		raw_skeleton: string,
		raw_atlas: string
	): Spine {
		let atlas = new TextureAtlas(raw_atlas, (line, callback) => {
			callback(PIXI.BaseTexture.from(name))
		})
		let spineAtlasLoader = new AtlasAttachmentLoader(atlas)
		let spineJsonParser = new SkeletonJson(spineAtlasLoader)

		let spineData = spineJsonParser.readSkeletonData(raw_skeleton)
		return new Spine(spineData)
	}
}
